import { useEffect, useState } from 'react';
import { Button, Input } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { usePayerDirectory } from '../hooks/usePayerDirectory';
import {
  useAddPayerManuallyThenAttach,
  useAddPayerToLocation,
} from '../hooks/useAddPayerToLocation';
import styles from './AddPayerPanel.module.css';

interface AddPayerPanelProps {
  onClose: () => void;
}

export function AddPayerPanel({ onClose }: AddPayerPanelProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const debounceTimer = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  const { results } = usePayerDirectory(debounced);
  const addExisting = useAddPayerToLocation();
  const addManually = useAddPayerManuallyThenAttach();

  return (
    <div className={styles.panel}>
      <div className={styles.searchRow}>
        <div className={styles.searchInput}>
          <Input
            placeholder="Search the payer directory… (e.g., Guardian, DentaQuest)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <span className={patterns.ctaLink} onClick={onClose}>
          close
        </span>
      </div>

      {debounced && (
        <div className={styles.results}>
          {results.length === 0 ? (
            <div className={patterns.cellMuted}>No matches in the directory.</div>
          ) : (
            results.map((payer) => (
              <div key={payer.id} className={styles.resultRow}>
                <span className={patterns.cellPrimary}>{payer.name}</span>
                <span
                  className={patterns.ctaLink}
                  onClick={() => addExisting.mutate(payer.id, { onSuccess: onClose })}
                >
                  + Add to this location
                </span>
              </div>
            ))
          )}
        </div>
      )}

      <div className={styles.footer}>
        <span>Can't find it?</span>
        <Button
          size="sm"
          variant="secondary"
          isLoading={addManually.isPending}
          disabled={!query.trim()}
          onClick={() => addManually.mutate(query.trim(), { onSuccess: onClose })}
        >
          Add "{query.trim() || '…'}" manually
        </Button>
        <span>— it's created as pending and our team charts its phone system.</span>
      </div>
    </div>
  );
}
